import '.x{width:100%;}.ssr-plugin'
import '.ac{border-radius:1.25rem;}.ssr-plugin'
import '.ae{overflow:hidden;}.ssr-plugin'
import '.gd{min-height:12rem;}.ssr-plugin'
import '.c8{position:relative;}.ssr-plugin'
import '.b9{position:absolute;}.ssr-plugin'
import '.ct{top:0px;}.ssr-plugin'
import '.g0{bottom:0px;}.ssr-plugin'
import '.bh{right:0px;}.ssr-plugin'
import '.ge{z-index:1;}.ssr-plugin'
import '.gf{z-index:2;}.ssr-plugin'
import '.av{width:fit-content;}.ssr-plugin'
import { Button, ButtonProps } from '@app/components/core/button/Button'
import { ProfileProps } from './PageHeader'
import { AvatarStack, ProfileAvatar } from '../avatarStack/AvatarStack'
import { StoredImage } from '@app/types/StoredImage'
import Row from '@app/components/layout/Row'
import { Card } from '../card/Card'
import Column from '@app/components/layout/Column'
import useMediaQuery from '@app/util/useMediaQuery'
import { Title } from '@app/components/core/typography/Title'
import { Badge } from '@app/components/core/badge/Badge'
import { Text } from '@app/components/core/typography/Text'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { pages } from '@app/util/links'
import { TextLink } from '@app/components/core/link/TextLink'
import { navigate } from 'wouter/use-browser-location'
import { Image } from '@app/components/core/image/Image'
import { styled, type Style } from 'inlines'

export type RegionalPageHeaderProps = {
  title: string
  subtitle?: string
  banner?: StoredImage
  buttons?: ButtonProps[]
  hasBadge?: boolean
  profileRole?: string
  profile?: ProfileProps
  followers?: ProfileAvatar[]
  totalFollowers?: number
  disableAnimations?: boolean
}

const BANNER_WIDTH = '66.666%'
const CARD_MAX_WIDTH = BANNER_WIDTH
const HEADER_VERTICAL_INSET = '1rem'
const HEADER_SLIDE_BACK_DURATION = '700ms'
const HEADER_SLIDE_BACK_DELAY = '500ms'
const HEADER_SLIDE_BACK_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)'

const RegionalPageHeaderCard = ({
  title,
  subtitle,
  buttons,
  hasBadge,
  profileRole,
  profile,
  followers,
  totalFollowers,
  style,
}: Omit<RegionalPageHeaderProps, 'banner'> & { style?: Style }) => (
  <Card
    style={{
      borderRadius: '1.25rem',
      boxShadow: '0 0 24px 0 rgba(0, 0, 0, 0.15)',
      width: 'auto',
      ...style,
    }}
  >
    <Column gap="2rem">
      <Column gap="0.5rem">
        <Row gap="1rem" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Title.H1
            style={{
              marginBottom: 0,
              maxWidth: '100%',
              overflowWrap: 'anywhere',
            }}
          >
            {title}
          </Title.H1>
          {hasBadge && <Badge style={{ height: '2rem', width: '2rem' }} />}
        </Row>
        <Text.Large style={{ marginBottom: 0 }}>{subtitle}</Text.Large>
      </Column>
      {profile && (
        <Row
          gap={'0.5rem'}
          style={{
            marginBottom: '2rem',
            alignItems: 'center',
          }}
        >
          {profileRole && (
            <Text.Medium style={{ marginBottom: 0 }}>{profileRole}</Text.Medium>
          )}
          <Row
            gap={'0.35rem'}
            style={{ cursor: 'pointer', alignItems: 'center' }}
          >
            <Avatar
              image={profile.avatar?.src}
              size="extra-extra-small"
              shape="square"
              onClick={() =>
                navigate(pages.regionalBusinessProfile(profile.slug))
              }
              style={{
                border: 'var(--border-subtle)',
              }}
            />
            <TextLink
              to={pages.regionalBusinessProfile(profile.slug)}
              textStyle={{
                fontSize: '1rem',
                fontWeight: 500,
                color: 'var(--brand-secondary)',
                textDecoration: 'none',
              }}
            >
              {profile.title}
            </TextLink>
          </Row>
        </Row>
      )}

      {followers && (
        <AvatarStack
          size={'extra-small'}
          avatars={followers}
          total={totalFollowers}
          style={{
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            paddingBottom: '1.5rem',
          }}
        />
      )}

      {buttons && (
        <Row>
          {[...buttons].map((button, index) => (
            <Button key={`${button.label}-${index}`} {...button} />
          ))}
        </Row>
      )}
    </Column>
  </Card>
)

export const RegionalPageHeader = ({
  title,
  subtitle,
  banner,
  buttons,
  hasBadge,
  profileRole,
  profile,
  followers,
  totalFollowers,
  disableAnimations,
}: RegionalPageHeaderProps) => {
  const isMobile = useMediaQuery()
  const bannerSrc = banner?.src?.trim()

  const cardProps = {
    title,
    subtitle,
    buttons,
    hasBadge,
    profileRole,
    profile,
    followers,
    totalFollowers,
  }

  const bannerAnimation = disableAnimations
    ? undefined
    : `regional-header-banner-slide-back ${HEADER_SLIDE_BACK_DURATION} ${HEADER_SLIDE_BACK_EASING} ${HEADER_SLIDE_BACK_DELAY} both`

  const cardAnimation = disableAnimations
    ? undefined
    : `regional-header-card-slide-back ${HEADER_SLIDE_BACK_DURATION} ${HEADER_SLIDE_BACK_EASING} ${HEADER_SLIDE_BACK_DELAY} both`

  if (!bannerSrc) {
    return (
      <Column gap="1rem" alignItems="start">
        <RegionalPageHeaderCard {...cardProps} />
      </Column>
    )
  }

  if (isMobile) {
    return (
      <Column gap="1rem" alignItems="stretch">
        <RegionalPageHeaderCard {...cardProps} />
        <div className="x ac ae gd"
        >
          <Image
            src={bannerSrc}
            alt={banner?.altText ?? `${title} banner image`}
            style={{
              display: 'block',
              width: '100%',
              height: '12rem',
              objectFit: 'cover',
            }}
          />
        </div>
      </Column>
    )
  }

  return (
    <div className="c8 x"
      style={{
        paddingTop: HEADER_VERTICAL_INSET,
        paddingBottom: HEADER_VERTICAL_INSET,
      }}
    >
      <div className="b9 ct g0 bh ge ac ae"
        style={{
          width: BANNER_WIDTH,
          animation: bannerAnimation,
        }}
      >
        <Image
          src={bannerSrc}
          alt={banner?.altText ?? `${title} banner image`}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            minHeight: '100%',
            objectFit: 'cover',
          }}
        />
      </div>

      <div className="c8 gf av"
        style={{
          maxWidth: CARD_MAX_WIDTH,
          animation: cardAnimation,
        }}
      >
        <RegionalPageHeaderCard {...cardProps} />
      </div>
    </div>
  )
}
